import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  RouteWidgetBaseParameters,
  TruckParameters,
} from '@pages/AdditionalServices/tabs/DeliveryForm/types';
import { LocalizedMessage } from '@services/requests/localizedMessagesService/interfaces';
import React, { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { checkEmptyInputValue } from '../../../helpers';
import useDelivery from '../../../useDelivery';
import { CrossDockingPalletsParameters } from '../CrossDockingPalletsParameters';
import TruckTypeInformation from '../TruckTypeInformation';

// Свойства компонента
export interface TruckParametersItemComponentProps {
  Position: number;
  BaseData: RouteWidgetBaseParameters;
  TruckParameters: TruckParameters;
  mainLanguage: string; // Текущий основной язык
  systemLanguage: string; // Системный язык
  isDeletable: boolean;
  onChange: { (params: TruckParameters, field: keyof TruckParameters): void };
  onDelete: { (): void };
  onChangeCargoFieldsFilled: { (values: boolean[]): void };
}

const TruckParametersItemComponent: FC<TruckParametersItemComponentProps> = ({
  BaseData,
  TruckParameters,
  onDelete,
  isDeletable,
  onChange,
  Position,
  systemLanguage,
  onChangeCargoFieldsFilled,
}) => {
  const { useRouteParams, useIsRoutesLoading, useIsLoading } = useDelivery();
  const [routeParams, setRouteParams] = useRouteParams().useState();
  const [isRoutesLoading] = useIsRoutesLoading().useState();
  const [isLoading] = useIsLoading().useState();
  const { t } = useTranslation('Common');
  const { trucks, units } = BaseData;

  const isQuantityInputInvalid = checkEmptyInputValue(TruckParameters.truckQuantity);
  const isWeightInputInvalid = checkEmptyInputValue(TruckParameters.eachTruckWeight);

  const palletUnits = useMemo(() => {
    const foundTruck = trucks.find((c) => c.id === TruckParameters.truckId?.toString());
    const uniquePalletIds = Array.from(
      new Set((foundTruck?.countPallets ?? []).map((p) => p.type_pallet))
    );

    return uniquePalletIds
      .map((palletId) => units.find((unit) => unit.id === palletId))
      .filter(Boolean);
  }, [trucks, units, TruckParameters.truckId]);

  useEffect(() => {
    onChangeCargoFieldsFilled([isWeightInputInvalid, isQuantityInputInvalid]);
  }, [isWeightInputInvalid, isQuantityInputInvalid]);

  useEffect(() => {
    const { truckId } = TruckParameters;
    const defaultTruck = trucks[0];

    if (!truckId) {
      setRouteParams((prevState) => ({
        ...prevState,
        truckParameters: {
          ...prevState.truckParameters,
          truckParameters: prevState.truckParameters.truckParameters.map((t) => ({
            ...t,
            truckId: t.truckId ?? defaultTruck.id,
            eachTruckWeight: t.eachTruckWeight ?? 1500,
          })),
        },
      }));
    }
  }, [trucks]);

  /**
   * Обработка изменения одного из значений параметров фуры
   * @param property
   * @param value
   */
  const handleChangeTruckProperty = <T extends keyof TruckParameters>(
    property: T,
    value: TruckParameters[T]
  ) => {
    const params = { ...TruckParameters };
    params[property] = value;

    onChange(params, property);
  };

  /**
   * Получение локализованного названия
   * @param defaultName
   * @param localizations
   */
  const handleGenerateLocalizedName = (
    defaultName: string,
    localizations: LocalizedMessage[]
  ): string => {
    const localization = localizations.find((l) => l.lang_id === systemLanguage);
    if (!localization || localization.message.length === 0) {
      return defaultName;
    }

    return localization.message;
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Typography
        variant={'subtitle2'}
        color={'grey[700]'}
        marginBottom={'15px'}
        sx={{
          mb: {
            sx: 15,
            md: 18,
            lg: 15,
          },
        }}
        textTransform={'capitalize'}
      >
        {t('Common:Auto')} #{Position}
      </Typography>
      <Box className={'cargo-inputs-block'}>
        <Box width={'100%'}>
          <TextField
            fullWidth
            variant={'standard'}
            value={TruckParameters.truckId ?? ''}
            select
            sx={{
              '& .MuiInput-input': {
                color: '#383838!important',
                fontStyle: 'normal!important',
                fontWeight: '600!important',
                lineHeight: '22px!important',
                textAlign: 'right!important',
                fontSize: '12px!important',
              },
            }}
            disabled={trucks.length === 0 || isRoutesLoading || isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ margin: '0' }}>
                  <Typography
                    sx={{ color: 'grey[700]', position: 'absolute', pointerEvents: 'none' }}
                    component={'span'}
                    variant={'h5'}
                    className={'cargo-inputs-block__text-field'}
                  >
                    {t('Common:VolumeTuck')}
                    <Tooltip
                      title={
                        <TruckTypeInformation trucks={trucks} units={units} lang={systemLanguage} />
                      }
                      enterTouchDelay={0}
                      leaveTouchDelay={10000}
                      arrow
                    >
                      <span className={'cargo-inputs-block__tooltip'}>?</span>
                    </Tooltip>
                  </Typography>
                </InputAdornment>
              ),
            }}
            onChange={(event) => handleChangeTruckProperty('truckId', event.target.value)}
          >
            {trucks.map((option) => (
              <MenuItem value={option.id} key={option.id}>
                {handleGenerateLocalizedName(option.default_name, option.localized_names_data)}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <TextField
          error={isQuantityInputInvalid}
          focused={isQuantityInputInvalid}
          fullWidth
          variant={'standard'}
          value={TruckParameters.truckQuantity}
          type={'number'}
          disabled={isRoutesLoading || isLoading}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment position="start">
                <Typography
                  sx={{ color: 'grey[700]', position: 'absolute', pointerEvents: 'none' }}
                  component={'span'}
                  variant={'h5'}
                >
                  {t('Common:QuantityTruckPcs')}
                </Typography>
              </InputAdornment>
            ),
          }}
          onFocus={() => handleChangeTruckProperty('truckQuantity', parseInt(''))}
          onChange={(event) =>
            handleChangeTruckProperty('truckQuantity', parseInt(event.target.value))
          }
        />

        <TextField
          error={isWeightInputInvalid}
          focused={isWeightInputInvalid}
          fullWidth
          variant={'standard'}
          value={TruckParameters.eachTruckWeight}
          type={'number'}
          disabled={isRoutesLoading || isLoading}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment position="start">
                <Typography
                  sx={{ color: 'grey[700]', position: 'absolute', pointerEvents: 'none' }}
                  component={'span'}
                  variant={'h5'}
                >
                  {t('OrderList:EachTruckWeight')}
                </Typography>
              </InputAdornment>
            ),
          }}
          onFocus={() => handleChangeTruckProperty('eachTruckWeight', parseInt(''))}
          onChange={(event) =>
            handleChangeTruckProperty('eachTruckWeight', parseInt(event.target.value))
          }
        />
        <CrossDockingPalletsParameters
          cargoType="truck"
          withPallets={TruckParameters.withPallets}
          palletsLoaded={TruckParameters.palletsLoaded}
          isActionsDisabled={isRoutesLoading || isLoading}
          isCrossDockingEnabled={routeParams.isWithCrossDocking}
          availablePalletUnits={palletUnits}
          Position={Position}
          onChangeValueByProperty={handleChangeTruckProperty}
          onGenerateLocalizedName={handleGenerateLocalizedName}
        />
      </Box>
      {isDeletable && (
        <IconButton
          sx={{
            position: 'absolute',
            right: '-15px',
            top: '-15px',
          }}
          disabled={isRoutesLoading || isLoading}
          size={'small'}
          onClick={() => onDelete()}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default TruckParametersItemComponent;
